import { cx } from "components/Graphs/utils";
import React from "react";

export type ApplicationPageLayoutMode = "centered" | "fluid";

const ApplicationPageLayoutContext =
  React.createContext<ApplicationPageLayoutMode>("centered");

export const useApplicationPageLayoutMode = () =>
  React.useContext(ApplicationPageLayoutContext);

export interface ApplicationPageLayoutProviderProps {
  mode: ApplicationPageLayoutMode;
  children: React.ReactNode;
}

export const ApplicationPageLayoutProvider: React.FC<
  ApplicationPageLayoutProviderProps
> = ({ mode, children }) => (
  <ApplicationPageLayoutContext.Provider value={mode}>
    {children}
  </ApplicationPageLayoutContext.Provider>
);

export type ApplicationPageLayoutProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Shared route-level content boundary. It preserves the existing centered
 * 80rem layout by default and becomes a left-aligned, fluid 100rem layout
 * when rendered inside the redesigned application shell.
 */
export const ApplicationPageLayout = React.forwardRef<
  HTMLDivElement,
  ApplicationPageLayoutProps
>(function ApplicationPageLayout({ className, ...props }, forwardedRef) {
  const mode = useApplicationPageLayoutMode();

  return (
    <div
      {...props}
      ref={forwardedRef}
      data-application-page-layout={mode}
      className={cx(
        "w-full",
        mode === "fluid" ? "mx-0 max-w-[100rem]" : "mx-auto max-w-7xl",
        className,
      )}
    />
  );
});

ApplicationPageLayout.displayName = "ApplicationPageLayout";

export default ApplicationPageLayout;
